import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { observer } from 'mobx-react-lite';
import { useLocation } from 'react-router-dom';

import { Studio } from '../types.ts/general';
import { getCurrentStudio } from '../utility/general';
import MyAudioQuerySubscriptionComponent from './MyAudioQuerySubscriptionComponent';
import OrganizationArtifactsAiTitleSubscription from './OrganizationArtifactAiTitleSubscription';
import OrganizationArtifactsVideoSubscription from './OrganizationArtifactsVideoSubscription';
import PublishRecordQuerySubscriptionComponent from './PublishRecordQuerySubscriptionComponent';
import StoryArtifactsAiTitleSubscription from './StoryArtifactAiTitleSubscription';
import StoryArtifactsVideoSubscription from './StoryArtifactsVideoSubscription';

const logger = LogManager.getLogger(
  'component:GlobalQuerySubscriptionComponent',
);

const GlobalQuerySubscriptionComponent = observer(() => {
  const videoCreator = useVideoCreatorStore();

  const { pathname } = useLocation();
  const currentStudio = getCurrentStudio(pathname);
  const generatingMyAudio =
    videoCreator.story?.myAudios?.filter(
      o => o.metadataStatus === 'generating' && !o.waveformUploadId,
    ) || [];

  const generatingOrganizationAudio =
    videoCreator.organization?.organizationAudios?.filter(
      o => o.metadataStatus === 'generating' && !o.waveformUploadId,
    ) || [];

  const storyIdsWithWatchedArtifacts =
    videoCreator.statusUpdateManager.getStoryIdsWithWatchedArtifacts();
  const organizationIdsWithWatchedArtifacts =
    videoCreator.statusUpdateManager.getOrganizationIdsWithWatchedArtifacts();
  const storyIdsWithWatchedVideoArtifacts =
    videoCreator.statusUpdateManager.getStoryIdsWithWatchedVideoArtifacts();
  const organizationIdsWithWatchedVideoArtifacts =
    videoCreator.statusUpdateManager.getOrganizationIdsWithWatchedVideoArtifacts();

  // Successor to renderingVideoIds — the in-flight PublishRecord id set the
  // live subscription watches (per-variant render tracking). Copy to a plain
  // array so the `key`/prop are stable snapshots.
  const publishTrackingInFlightIds =
    videoCreator.rootStore.publishTrackingStore.inFlightIds.slice();

  if (!currentStudio) return null;

  return (
    <>
      {!!publishTrackingInFlightIds.length && (
        <PublishRecordQuerySubscriptionComponent
          key={publishTrackingInFlightIds.join('_')}
          recordIds={publishTrackingInFlightIds}
          onError={error => {
            logger.error(
              'PublishRecordQuerySubscriptionComponent error',
              extractErrorInfo(error),
            );
          }}
          onDataReceived={async updates => {
            // Merge the lean pushes into the tracking store (prunes finished
            // ids), then let the store's owner apply the render results to the
            // legacy card/currentVideo in memory.
            const merged =
              videoCreator.rootStore.publishTrackingStore.applyRecords(updates);
            await videoCreator.applyRenderedPublishRecords(merged);
          }}
        />
      )}

      {(currentStudio === Studio.creator || currentStudio === Studio.content) &&
        !!generatingMyAudio.length && (
          <MyAudioQuerySubscriptionComponent
            key={generatingMyAudio[0].id}
            audioIds={generatingMyAudio.map(audio => audio.id)}
            onError={error => {
              logger.error(
                'MyAudioQuerySubscriptionComponent error',
                extractErrorInfo(error),
              );
            }}
            onDataReceived={async audios => {
              logger.info('audio processed', { count: audios.length });
              videoCreator.handleUpdateMyAudioMetadata(audios);
            }}
          />
        )}

      {currentStudio === Studio.creator &&
        !!generatingOrganizationAudio.length && (
          <MyAudioQuerySubscriptionComponent
            key={generatingOrganizationAudio[0].id}
            audioIds={generatingOrganizationAudio.map(audio => audio.id)}
            onError={error => {
              logger.error(
                'MyAudioQuerySubscriptionComponent (organization) error',
                extractErrorInfo(error),
              );
            }}
            onDataReceived={async audios => {
              logger.info('audio processed (organization)', {
                count: audios.length,
              });
              videoCreator.handleUpdateOrganizationAudioMetadata(audios);
            }}
          />
        )}
      {!!storyIdsWithWatchedArtifacts.length && (
        <StoryArtifactsAiTitleSubscription
          storyIds={storyIdsWithWatchedArtifacts}
          onError={error => {
            logger.error(
              'StoryArtifactsAiTitleSubscription error',
              extractErrorInfo(error),
            );
          }}
          onDataReceived={updates => {
            videoCreator.statusUpdateManager.updateWatchedStoryArtifacts(
              updates,
            );
          }}
        />
      )}
      {!!organizationIdsWithWatchedArtifacts.length && (
        <OrganizationArtifactsAiTitleSubscription
          organizationIds={organizationIdsWithWatchedArtifacts}
          onError={error => {
            logger.error(
              'OrganizationArtifactsAiTitleSubscription error',
              extractErrorInfo(error),
            );
          }}
          onDataReceived={updates => {
            videoCreator.statusUpdateManager.updateWatchedOrganizationArtifacts(
              updates,
            );
          }}
        />
      )}
      {!!storyIdsWithWatchedVideoArtifacts.length && (
        <StoryArtifactsVideoSubscription
          storyIds={storyIdsWithWatchedVideoArtifacts}
          onError={error => {
            logger.error(
              'StoryArtifactsVideoSubscription error',
              extractErrorInfo(error),
            );
          }}
          onDataReceived={updates => {
            videoCreator.statusUpdateManager.updateWatchedStoryVideoArtifacts(
              updates,
            );
          }}
        />
      )}
      {!!organizationIdsWithWatchedVideoArtifacts.length && (
        <OrganizationArtifactsVideoSubscription
          organizationIds={organizationIdsWithWatchedVideoArtifacts}
          onError={error => {
            logger.error(
              'OrganizationArtifactsVideoSubscription error',
              extractErrorInfo(error),
            );
          }}
          onDataReceived={updates => {
            videoCreator.statusUpdateManager.updateWatchedOrganizationVideoArtifacts(
              updates,
            );
          }}
        />
      )}
    </>
  );
});

export default GlobalQuerySubscriptionComponent;
